<script setup lang="ts">
import { computed, provide, reactive, toRefs, useTemplateRef, watch } from "vue";

import { cardActions } from "@/action/cardActions";
import { stickyNoteActivated, stickyNoteDeactivated } from "@/analytics/events/sticky";
import { trackEvent } from "@/analytics/track";
import ActionMenu from "@/components/ActionMenu/ActionMenu.vue";
import { boardKey, boardMetaKey } from "@/components/board/injectKeys";
import { isBacklogBoard } from "@/model/board";
import type { Card, CardMeta } from "@/model/card";
import { useBoardStore } from "@/store/board";
import { useLinkStore } from "@/store/link";
import { usePanModeStore } from "@/store/panMode";
import { useUserStore } from "@/store/user";
import { injectStrict } from "@/utils/vue";

import * as animations from "./animations";
// composables
import { useDrag } from "./composables/useDrag";
import { useEnlarge } from "./composables/useEnlarge";
import { useLinkDrag } from "./composables/useLinkDrag";
import { usePin } from "./composables/usePin";
import { useSelect } from "./composables/useSelect";
import { useStickyActionMenu } from "./composables/useStickyActionMenu";
import { useStyles } from "./composables/useStyles";
import { cardKey, cardMetaKey, cardMethodsKey } from "./injectKeys";
// sticky note elements
import StickyNoteAccessibilityControls from "./internal/Accessibility/StickyNoteAccessibilityControls.vue";
import StickyActionMenu from "./internal/ActionMenu/StickyActionMenu.vue";
import StickyNoteFilter from "./internal/StickyNoteFilter/StickyNoteFilter.vue";
import StickyNoteFooter from "./internal/StickyNoteFooter/StickyNoteFooter.vue";
import StickyNoteHeader from "./internal/StickyNoteHeader/StickyNoteHeader.vue";
import StickyNoteLinks from "./internal/StickyNoteLinks/StickyNoteLinks.vue";
import StickyNoteLocked from "./internal/StickyNoteLocked/StickyNoteLocked.vue";
import StickyNotePin from "./internal/StickyNotePin/StickyNotePin.vue";
import StickyNoteProgress from "./internal/StickyNoteProgress/StickyNoteProgress.vue";
import StickyNoteText from "./internal/StickyNoteText/StickyNoteText.vue";

type Props = Readonly<{
  card: Card;
  cardMeta: CardMeta;
  noDrag?: boolean;
  fullDetails?: boolean;
  readonly?: boolean;
  levelOfDetails?: 0 | 1 | 2;
}>;

const props = withDefaults(defineProps<Props>(), { levelOfDetails: 0 });

const board = injectStrict(boardKey);
const boardMeta = injectStrict(boardMetaKey);

const levelOfDetails = computed(() => {
  if (props.fullDetails || isEnlarged.value || isEnlarging.value || isActive.value) {
    return 2;
  }
  return props.levelOfDetails;
});

const { card, cardMeta, noDrag } = toRefs(props);

const stickyNoteRef = useTemplateRef("stickyNote");
const textInputRef = useTemplateRef<typeof StickyNoteText>("textInput");
const accessibilityControlsRef = useTemplateRef("accessibilityControls");

const isPanModeActive = computed(() => usePanModeStore().active);

// someone else is editing
const isLocked = computed(() => !!props.card.editor?.id);
// there are more than one card selected on the current board
const isMultiSelecting = computed(() => useBoardStore().areMultipleStickiesSelected);
const isActive = computed(() => useBoardStore().activeCardId === props.card.id);

// used for analytics
watch(isActive, (newVal, prevVal) => {
  const cardType = props.card.type.functionality;

  if (newVal && !prevVal) {
    trackEvent(stickyNoteActivated(cardType));
  } else {
    trackEvent(stickyNoteDeactivated(cardType));
  }
});

const {
  isEnlarged,
  isEnlarging,
  classes: enlargeTransitionClasses,
} = useEnlarge({ el: stickyNoteRef, card: props.card, disabled: isLocked });

const { isSelected, deactivateSticky, tabindex, wasActivatedByKeyboard } = useSelect({
  el: stickyNoteRef,
  textInput: textInputRef,
  card: props.card,
  board: board,
  cardMeta: props.cardMeta,
});

const { styles, sizes, position } = useStyles({
  card,
  cardMeta,
  board,
  isEnlarged,
  isActive,
  boardMeta,
});

const isPinningDisabled = computed(
  () =>
    isPanModeActive.value || isEnlarged.value || isLocked.value || isBacklogBoard(board.value.type),
);
usePin({ el: stickyNoteRef, card: props.card, disabled: isPinningDisabled });

const isReadonly = computed(
  () => props.readonly || isPanModeActive.value || !useUserStore().isAllowed("edit"),
);

const isDraggingDisabled = computed(
  () => isEnlarged.value || noDrag.value || isLocked.value || isReadonly.value,
);
useDrag({ el: stickyNoteRef, card: props.card, disabled: isDraggingDisabled });

const isActionMenuDisabled = computed(
  () => isLocked.value || isEnlarging.value || isReadonly.value,
);
const actionMenu = useStickyActionMenu({
  referenceEl: stickyNoteRef,
  card: props.card,
  disabled: isActionMenuDisabled,
});

const { isLinkingTarget, isLinkingSource } = useLinkDrag({
  el: stickyNoteRef,
  card: props.card,
  disabled: isLocked,
});

provide(cardKey, props.card);
provide(
  cardMetaKey,
  reactive({
    rootEl: stickyNoteRef.value,
    pos: props.cardMeta.pos,
    isPinningDisabled,
    isSelected,
    isLinkingTarget,
    isEnlarged,
    levelOfDetails,
    isReadonly,
    wasActivatedByKeyboard,
  }),
);

provide(cardMethodsKey, {
  animate: async (animation) => {
    switch (animation) {
      case "mirroring":
        return await animations.mirror(stickyNoteRef.value);
      case "deleting":
        return await animations.remove(stickyNoteRef.value);
      case "moving":
        return await animations.move(stickyNoteRef.value);
      default:
        throw Error(`Unknown animation: ${animation}`);
    }
  },
});

/**
 * Enter move-by-keyboard mode when the user presses 'm' on the sticky directly
 */
const handleMoveHotkey = (e: KeyboardEvent) => {
  if (e.target !== e.currentTarget) return;
  accessibilityControlsRef.value?.focusAndStartMove();
};

/**
 * Ensure the sticky note is deactivated (if necessary) and focused
 */
const handleAccessibleDeactivate = () => {
  if (isActive.value) {
    deactivateSticky();
  } else {
    stickyNoteRef.value?.focus();
  }
};
function handlePinIconClick() {
  cardActions.unpin("mouse", card.value.id);
}
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions // extra shortcut -->
  <div
    :id="card.id"
    ref="stickyNote"
    data-no-rectangle-select
    :class="[
      'sticky-note',
      {
        ...enlargeTransitionClasses,
        'linking-target': isLinkingTarget,
        draggable: !isDraggingDisabled,
        selected: isSelected,
      },
    ]"
    :style="styles"
    v-bind="$attrs"
    :role="
      isActive
        ? 'region' /* Act as a region when active, so descendent buttons work for screen readers */
        : 'button' /* Act as a button when inactive, so user can 'enter' the sticky */
    "
    :tabindex
    :aria-label="$t('label.stickyNote', { type: card.type.name, text: card.text })"
    :aria-owns="isActive ? `action-menu-${props.card.id}` : ''"
    @keydown.m="handleMoveHotkey"
  >
    <StickyNotePin
      v-if="useLinkStore().isPinned(card.id) && !isEnlarged && !isEnlarging"
      @click="handlePinIconClick"
    />

    <StickyNoteHeader />
    <StickyNoteText ref="textInput" @deactivate-note="deactivateSticky" />
    <StickyNoteFooter />

    <StickyNoteProgress v-if="!isEnlarged" />
    <StickyNoteFilter :mark="cardMeta.mark" />
    <StickyNoteLocked v-if="card.editor && card.editor.color" :editor="card.editor" />
    <StickyNoteLinks
      v-if="
        (isActive || useLinkStore().isPinned(card.id)) &&
        !isEnlarging &&
        !isLinkingSource &&
        !isBacklogBoard(board.type) &&
        !isMultiSelecting
      "
      :scale="sizes.scale"
      :offset="position.offset"
    />
    <StickyNoteAccessibilityControls
      v-if="board.state.finishedTicks > 2"
      ref="accessibilityControls"
      @deactivate="handleAccessibleDeactivate"
    />
  </div>

  <ActionMenu :controller="actionMenu">
    <StickyActionMenu />
  </ActionMenu>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/variables";
@use "@/styles/mixins/shadow";
@use "@/styles/mixins/a11y";

.sticky-note {
  @include shadow.new-sticky-note;
  @include a11y.sticky-note;

  position: absolute;
  display: grid;
  grid-template-rows: min-content 1fr min-content;
  font-size: 12px;
  line-height: variables.$sticky-line-height;
  height: variables.$sticky-size;
  width: variables.$sticky-size;
  transform-origin: top left;

  &.draggable {
    cursor: grab;
  }

  &.selected {
    outline: 2px solid colors.$main;
    outline-offset: -2px;
  }

  &.enlarge-transition {
    transition-property: top, left, transform, z-index;
    transition-duration: 0.15s;
    transition-timing-function: ease-in-out;
    transform-origin: top left;
  }

  &.linking-target {
    filter: brightness(70%);
  }

  &.deleting {
    opacity: 0;
    transition: opacity 0.5s;
  }
}
</style>
